import { Modal, Typography } from "antd";
import {
  ArrowRightOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import {
  AssetDiffEntry,
  FieldChange,
  SiteIoDiff,
  flattenObject,
} from "./utils/siteIoDiffUtils";

type SiteIoConfigDiffModalProps = {
  open: boolean;
  diff: SiteIoDiff;
  onCancel: () => void;
  onOk?: () => void;
  confirmLoading?: boolean;
  mode: "preview" | "save";
};

function formatValue(value: unknown): string {
  if (value === undefined) {
    return "—";
  }
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function FieldChangeRow({ change }: { change: FieldChange }) {
  const { field, oldValue, newValue } = change;
  const isAdded = oldValue === undefined;
  const isRemoved = newValue === undefined;
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="font-mono text-gray-500 min-w-[180px]">{field}</span>
      {isAdded ? (
        <span className="text-green-600 font-medium break-all">
          + {formatValue(newValue)}
        </span>
      ) : isRemoved ? (
        <span className="text-red-500 line-through break-all">
          {formatValue(oldValue)}
        </span>
      ) : (
        <span className="flex items-center gap-1.5 flex-wrap">
          <span className="text-red-500 line-through break-all">
            {formatValue(oldValue)}
          </span>
          <ArrowRightOutlined className="text-[10px] text-gray-300" />
          <span className="text-green-600 font-medium break-all">
            {formatValue(newValue)}
          </span>
        </span>
      )}
    </div>
  );
}

function AssetCardHeader({
  assetId,
  badge,
  badgeColor,
  icon,
}: {
  assetId: string;
  badge: string;
  badgeColor: "green" | "red" | "amber";
  icon: React.ReactNode;
}) {
  const palette = {
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-800",
      chipBg: "bg-green-100",
      chipText: "text-green-600",
    },
    red: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-800",
      chipBg: "bg-red-100",
      chipText: "text-red-600",
    },
    amber: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-800",
      chipBg: "bg-amber-100",
      chipText: "text-amber-600",
    },
  }[badgeColor];
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 ${palette.bg} border-b ${palette.border}`}
    >
      {icon}
      <span className={`font-semibold text-sm font-mono ${palette.text}`}>
        {assetId}
      </span>
      <span
        className={`ml-auto text-[11px] font-medium ${palette.chipText} ${palette.chipBg} px-1.5 py-0.5 rounded`}
      >
        {badge}
      </span>
    </div>
  );
}

function AddedAssetCard({
  entry,
}: {
  entry: AssetDiffEntry & { kind: "added" };
}) {
  const flattened = flattenObject(entry.config);
  return (
    <div className="rounded-lg border border-green-200 overflow-hidden">
      <AssetCardHeader
        assetId={entry.assetId}
        badge="New asset"
        badgeColor="green"
        icon={<PlusCircleOutlined className="text-green-600 text-sm" />}
      />
      <div className="px-3 py-2 flex flex-col gap-1">
        {Object.entries(flattened).map(([field, value]) => (
          <FieldChangeRow
            key={field}
            change={{ field, oldValue: undefined, newValue: value }}
          />
        ))}
      </div>
    </div>
  );
}

function RemovedAssetCard({
  entry,
}: {
  entry: AssetDiffEntry & { kind: "removed" };
}) {
  const flattened = flattenObject(entry.config);
  return (
    <div className="rounded-lg border border-red-200 overflow-hidden">
      <AssetCardHeader
        assetId={entry.assetId}
        badge="Removed asset"
        badgeColor="red"
        icon={<MinusCircleOutlined className="text-red-500 text-sm" />}
      />
      <div className="px-3 py-2 flex flex-col gap-1">
        {Object.entries(flattened).map(([field, value]) => (
          <FieldChangeRow
            key={field}
            change={{ field, oldValue: value, newValue: undefined }}
          />
        ))}
      </div>
    </div>
  );
}

function ChangedAssetCard({
  entry,
}: {
  entry: AssetDiffEntry & { kind: "changed" };
}) {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <AssetCardHeader
        assetId={entry.assetId}
        badge="Modified asset"
        badgeColor="amber"
        icon={<EditOutlined className="text-amber-500 text-sm" />}
      />
      <div className="px-3 py-2 flex flex-col gap-1">
        {entry.changes.map((change) => (
          <FieldChangeRow key={change.field} change={change} />
        ))}
      </div>
    </div>
  );
}

function AssetEntry({ entry }: { entry: AssetDiffEntry }) {
  if (entry.kind === "added") {
    return <AddedAssetCard entry={entry} />;
  }
  if (entry.kind === "removed") {
    return <RemovedAssetCard entry={entry} />;
  }
  return <ChangedAssetCard entry={entry} />;
}

function Section<T>({
  title,
  entries,
  render,
}: {
  title: string;
  entries: T[];
  render: (entry: T, idx: number) => React.ReactNode;
}) {
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-2">
      <Typography.Text strong className="text-sm">
        {title} ({entries.length})
      </Typography.Text>
      <div className="flex flex-col gap-2">{entries.map(render)}</div>
    </div>
  );
}

export function SiteIoConfigDiffModal({
  open,
  diff,
  onCancel,
  onOk,
  confirmLoading,
  mode,
}: SiteIoConfigDiffModalProps) {
  const added = diff.assets.filter((a) => a.kind === "added");
  const changed = diff.assets.filter((a) => a.kind === "changed");
  const removed = diff.assets.filter((a) => a.kind === "removed");

  const fieldChanges = [
    diff.controllerModeChange,
    diff.phasePermutationModeChange,
  ].filter((change): change is FieldChange => change !== undefined);

  const totalChanges = diff.assets.length + fieldChanges.length;
  const isEmpty = totalChanges === 0;

  return (
    <Modal
      title={mode === "save" ? "Confirm Changes" : "Preview Changes"}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={confirmLoading}
      okText="Save"
      cancelText={mode === "save" ? "Cancel" : "Close"}
      footer={mode === "preview" ? null : undefined}
      width={680}
    >
      {isEmpty ? (
        <Typography.Text>No changes compared to the live site.</Typography.Text>
      ) : (
        <div className="flex flex-col gap-4">
          <Typography.Text type="secondary" className="text-xs">
            {totalChanges} change{totalChanges === 1 ? "" : "s"} compared to the
            live site
          </Typography.Text>
          <Section
            title="Controller fields"
            entries={fieldChanges}
            render={(change, i) => (
              <div
                key={`field-${i}`}
                className="rounded-lg border border-gray-200 overflow-hidden"
              >
                <AssetCardHeader
                  assetId={change.field}
                  badge="Modified field"
                  badgeColor="amber"
                  icon={<EditOutlined className="text-amber-500 text-sm" />}
                />
                <div className="px-3 py-2 flex flex-col gap-1">
                  <FieldChangeRow change={change} />
                </div>
              </div>
            )}
          />
          <Section
            title="Assets added"
            entries={added}
            render={(e, i) => <AssetEntry key={`a-${i}`} entry={e} />}
          />
          <Section
            title="Assets changed"
            entries={changed}
            render={(e, i) => <AssetEntry key={`c-${i}`} entry={e} />}
          />
          <Section
            title="Assets removed"
            entries={removed}
            render={(e, i) => <AssetEntry key={`r-${i}`} entry={e} />}
          />
        </div>
      )}
    </Modal>
  );
}
